import { memo, useCallback, useEffect, useState } from "react";
import { emit } from "@tauri-apps/api/event";
import { Icons } from "@/components/ui";
import { CliLogo } from "@/components/ui/cli-logo";
import { translate, useT } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import {
  useNotificationStore,
  type SystemToast,
} from "@/stores/notification-store";
import { ToastCard } from "./toast-card";
import {
  ToastActionButton,
  ToastLogoTile,
  ToastPill,
  type ToastPillAction,
} from "./toast-pill";
import {
  SYSTEM_ACTION_VISIBLE_MS,
  SYSTEM_INFO_VISIBLE_MS,
  systemToastDot,
} from "./toast-status";
import {
  BrowserToastLogos,
  ConnectionToastLogos,
} from "./connection-toast-logos";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { requestWorkToastTask } from "./work-toast-actions";

const LEVEL_ICON = {
  info: "Info",
  warning: "AlertTriangle",
  error: "CircleAlert",
} as const;

/** Runs a system toast's Tauri event action. Failures surface as a new
 *  "Action failed" system toast rather than silently swallowing the error. */
function systemToastEventAction(
  eventName: string | undefined,
  appWide?: boolean,
  revision?: string,
) {
  if (!eventName) return undefined;
  return async () => {
    try {
      if (eventName.startsWith("denker://connection-health-action/")) {
        await emit(eventName, {
          displayId: getWindowDisplayId(),
          revision: revision ?? null,
        });
      } else {
        await emit(eventName);
      }
    } catch (error) {
      useNotificationStore.getState().addSystemToast({
        id: `system-toast-event-error-${eventName}`,
        appWide,
        title: translate(toastsMessages, "actionFailed"),
        description: error instanceof Error ? error.message : String(error),
        level: "error",
      });
      throw error;
    }
  };
}

function SystemToastLogo({ toast }: { toast: SystemToast }) {
  if (toast.icon?.kind === "connection") {
    return (
      <ConnectionToastLogos
        appName={toast.icon.appName}
        iconUrl={toast.icon.iconUrl}
      />
    );
  }
  if (toast.icon?.kind === "browser") {
    return <BrowserToastLogos browserId={toast.icon.browserId} />;
  }
  if (toast.icon?.kind === "cli") {
    return (
      <ToastLogoTile testId="system-toast-logo-cli">
        <CliLogo cli={toast.icon.cli} className="h-4 w-4" />
      </ToastLogoTile>
    );
  }
  const Icon =
    Icons[
      toast.icon?.kind === "icon" ? toast.icon.name : LEVEL_ICON[toast.level]
    ];
  return (
    <ToastLogoTile testId="system-toast-logo-icon">
      <Icon className="h-[15px] w-[15px] text-secondary" />
    </ToastLogoTile>
  );
}

/** Wrapped in `memo`: `ToastStack` mounts one of these per visible system
 *  toast, and `onExpand`/`onCollapse` must stay referentially stable
 *  (id-based callbacks, not inline closures) for the memo to pay off. */
export const SystemToastPill = memo(function SystemToastPill({
  toast,
  expanded,
  onExpand,
  onCollapse,
}: {
  toast: SystemToast;
  expanded: boolean;
  onExpand: (id: string) => void;
  onCollapse: () => void;
}) {
  const t = useT(toastsMessages);
  const removeSystemToast = useNotificationStore((s) => s.removeSystemToast);
  const getSystemToastAction = useNotificationStore(
    (s) => s.getSystemToastAction,
  );
  const [hovered, setHovered] = useState(false);

  const dismiss = useCallback(() => {
    if (removeSystemToast(toast.id, toast.revision ?? null) && expanded)
      onCollapse();
  }, [expanded, onCollapse, removeSystemToast, toast.id, toast.revision]);

  const runAction =
    (action: () => void | Promise<void>, dismissOnSuccess = true) =>
    () => {
      const current = useNotificationStore
        .getState()
        .systemToasts.find((item) => item.id === toast.id);
      if (!current || current.revision !== toast.revision) return;
      try {
        const result = action();
        if (result instanceof Promise) {
          void result
            .then(() => {
              if (dismissOnSuccess) dismiss();
            })
            .catch(() => undefined);
        } else if (dismissOnSuccess) {
          dismiss();
        }
      } catch {
        // Keep the toast visible so the user can retry.
      }
    };

  const localPrimary = getSystemToastAction(toast.id);
  const primary =
    localPrimary ??
    systemToastEventAction(toast.actionEvent, toast.appWide, toast.revision);
  // The owner acknowledges mirrored actions by removing the exact occurrence.
  // Removing it here after emit could overtake the owner's approval handler.
  const mirroredConnectionPrimary =
    !localPrimary &&
    toast.actionEvent?.startsWith("denker://connection-health-action/");
  const secondary = systemToastEventAction(
    toast.secondaryActionEvent,
    toast.appWide,
    toast.revision,
  );
  const actions: ToastPillAction[] = [];
  if (toast.secondaryActionLabel && secondary) {
    actions.push({
      id: "secondary",
      label: toast.secondaryActionLabel,
      onClick: runAction(secondary),
    });
  }
  if (toast.actionLabel && primary) {
    actions.push({
      id: "primary",
      label: toast.actionLabel,
      testId: toast.actionTestId,
      onClick: runAction(primary, !mirroredConnectionPrimary),
    });
  }
  const hasAction = actions.length > 0;
  const pillActions: ToastPillAction[] = [
    ...actions,
    {
      id: "dismiss",
      label: t("dismissNotification"),
      icon: "X",
      onClick: dismiss,
    },
  ];
  const paused = hovered || expanded;

  useEffect(() => {
    if (toast.persistent || paused) return;
    const timer = window.setTimeout(
      dismiss,
      hasAction ? SYSTEM_ACTION_VISIBLE_MS : SYSTEM_INFO_VISIBLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [dismiss, hasAction, paused, toast.persistent]);

  const menuItems = [{ id: "dismiss", label: t("clear"), onSelect: dismiss }];
  const description = toast.description?.trim() ?? "";
  const logo = <SystemToastLogo toast={toast} />;

  if (expanded) {
    return (
      <ToastCard
        toastKey={`system:${toast.id}`}
        state={toast.level}
        logo={logo}
        title={toast.title}
        subtitle={
          toast.level === "info"
            ? t("levelSystem")
            : toast.level === "warning"
              ? t("levelWarning")
              : t("levelError")
        }
        menuItems={menuItems}
        onCollapse={onCollapse}
      >
        {description && (
          <p className="whitespace-pre-wrap break-words text-appkit-caption text-secondary">
            {description}
          </p>
        )}
        {hasAction && (
          <div className="flex justify-end gap-1.5">
            {actions.map((action) => (
              <ToastActionButton key={action.id} action={action} />
            ))}
          </div>
        )}
      </ToastCard>
    );
  }

  return (
    <ToastPill
      toastKey={`system:${toast.id}`}
      state={toast.level}
      logo={logo}
      title={toast.title}
      detail={description.split("\n")[0] ?? ""}
      detailLines={toast.twoLineDetail ? 2 : 1}
      dot={systemToastDot(toast.level)}
      actions={pillActions}
      actionsPinned={toast.actionsPinned || !hasAction}
      actionsBelow={toast.actionsBelow}
      menuItems={[]}
      onBodyClick={() => {
        if (!toast.task) {
          onExpand(toast.id);
          return;
        }
        requestWorkToastTask(toast.task);
        dismiss();
      }}
      onHoverChange={setHovered}
    />
  );
});
